import {
  ButtonHTMLAttributes,
  forwardRef,
  HTMLAttributes,
  ReactNode,
} from "react";
import styled, { css, keyframes } from "styled-components";
import { designSystemVars as vars } from "../tokens";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "danger"
  | "dangerSolid"
  | "success"
  | "ghost"
  | "ai";
export type ButtonSize = "sm" | "md" | "lg";
export type ButtonContentAlign = "start" | "center" | "end";

export type ButtonBaseProps = ButtonHTMLAttributes<HTMLButtonElement>;

export const ButtonBase = styled.button``;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  contentAlign?: ButtonContentAlign;
  fullWidth?: boolean;
  loading?: boolean;
  loadingLabel?: string;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

const variantStyles = {
  primary: css`
    border-color: ${vars.color.action.primary};
    background: ${vars.color.action.primary};
    color: ${vars.color.text.inverse};

    &:not(:disabled):hover {
      border-color: ${vars.color.action.primaryHover};
      background: ${vars.color.action.primaryHover};
    }

    &:not(:disabled):active {
      border-color: ${vars.color.action.primaryPressed};
      background: ${vars.color.action.primaryPressed};
    }
  `,
  secondary: css`
    border-color: ${vars.color.border.strong};
    background: ${vars.color.background.surface};
    color: ${vars.color.text.primary};

    &:not(:disabled):hover {
      border-color: ${vars.color.action.primaryHover};
      background: ${vars.color.action.primaryHover};
      color: ${vars.color.text.inverse};
    }

    &:not(:disabled):active {
      border-color: ${vars.color.action.primaryPressed};
      background: ${vars.color.action.primaryPressed};
      color: ${vars.color.text.inverse};
    }
  `,
  danger: css`
    border-color: ${vars.color.border.danger};
    background: ${vars.color.background.surface};
    color: ${vars.color.text.danger};

    &:not(:disabled):hover {
      background: ${vars.color.action.danger};
      color: ${vars.color.text.inverse};
    }

    &:not(:disabled):active {
      background: ${vars.color.action.dangerHover};
      color: ${vars.color.text.inverse};
    }
  `,
  dangerSolid: css`
    border-color: ${vars.color.action.danger};
    background: ${vars.color.action.danger};
    color: ${vars.color.text.inverse};

    &:not(:disabled):hover,
    &:not(:disabled):active {
      border-color: ${vars.color.action.dangerHover};
      background: ${vars.color.action.dangerHover};
    }
  `,
  success: css`
    border-color: ${vars.color.action.success};
    background: ${vars.color.action.success};
    color: ${vars.color.text.inverse};

    &:not(:disabled):hover,
    &:not(:disabled):active {
      border-color: ${vars.color.action.successHover};
      background: ${vars.color.action.successHover};
    }
  `,
  ghost: css`
    border-color: var(--inzeloop-ds-color-border-transparent);
    background: var(--inzeloop-ds-color-background-transparent);
    color: ${vars.color.text.primary};

    &:not(:disabled):hover {
      background: ${vars.color.action.primary};
      color: ${vars.color.text.inverse};
    }

    &:not(:disabled):active {
      background: ${vars.color.action.primaryPressed};
      color: ${vars.color.text.inverse};
    }
  `,
  ai: css`
    border-color: var(--inzeloop-ds-color-border-transparent);
    background: ${vars.color.action.aiGradient};
    color: ${vars.color.text.inverse};

    &:not(:disabled):hover,
    &:not(:disabled):active {
      background: ${vars.color.action.aiGradientHover};
    }
  `,
};

const sizeStyles = {
  sm: css`
    min-height: 32px;
    padding: var(--inzeloop-ds-spacing-7px) ${vars.spacing.md};
    font-size: ${vars.typography.size.sm};
    line-height: ${vars.typography.lineHeight.xs};
  `,
  md: css`
    min-height: 40px;
    padding: var(--inzeloop-ds-spacing-7px) ${vars.spacing.lg};
    font-size: ${vars.typography.size.sm};
    line-height: ${vars.typography.lineHeight.sm};
  `,
  lg: css`
    min-height: 48px;
    padding: ${vars.spacing.md} ${vars.spacing.xl};
    font-size: ${vars.typography.size.md};
    line-height: ${vars.typography.lineHeight.md};
  `,
};

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const LoadingSpinner = styled.span`
  width: 1em;
  height: 1em;
  flex: 0 0 auto;
  border: 2px solid currentColor;
  border-right-color: ${vars.color.border.transparent};
  border-radius: ${vars.radius.full};
  animation: ${spin} ${vars.motion.processing} linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const StableButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
`;

const ButtonContentState = styled.span<{
  $visible: boolean;
  $contentAlign: ButtonContentAlign;
  $overlay?: boolean;
}>`
  ${({ $overlay }) =>
    $overlay &&
    css`
      position: absolute;
      inset: 0;
      overflow: hidden;
    `}
  display: inline-flex;
  align-items: center;
  justify-content: ${({ $contentAlign }) =>
    $contentAlign === "start"
      ? "flex-start"
      : $contentAlign === "end"
        ? "flex-end"
        : "center"};
  gap: ${vars.spacing.xs};
  visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
`;

const StyledButton = styled(ButtonBase)<{
  $variant: ButtonVariant;
  $size: ButtonSize;
  $fullWidth: boolean;
  $contentAlign: ButtonContentAlign;
}>`
  box-sizing: border-box;
  position: relative;
  display: inline-flex;
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "fit-content")};
  align-items: center;
  justify-content: ${({ $contentAlign }) =>
    $contentAlign === "start"
      ? "flex-start"
      : $contentAlign === "end"
        ? "flex-end"
        : "center"};
  gap: ${vars.spacing.xs};
  border: ${vars.borderWidth.thin} solid ${vars.color.border.transparent};
  border-radius: ${vars.radius.sm};
  font-family: ${vars.typography.family.sans};
  font-weight: ${vars.typography.weight.semibold};
  white-space: nowrap;
  cursor: default;
  transition:
    background-color ${vars.motion.fast} ${vars.motion.easing},
    border-color ${vars.motion.fast} ${vars.motion.easing},
    color ${vars.motion.fast} ${vars.motion.easing};

  ${({ $variant }: { $variant: ButtonVariant }) => variantStyles[$variant]}
  ${({ $size }: { $size: ButtonSize }) => sizeStyles[$size]}

  &:focus-visible {
    outline: none;
    box-shadow: ${vars.shadow.focus};
  }

  &:disabled:not([aria-busy="true"]) {
    border-color: ${vars.color.action.disabled};
    background: ${vars.color.action.disabled};
    color: ${vars.color.text.disabled};
    cursor: not-allowed;
  }

  &[aria-busy="true"] {
    cursor: wait;
  }

  svg {
    flex: 0 0 auto;
  }

  svg [stroke]:not([stroke="none"]) {
    stroke: currentColor;
  }

  svg [fill]:not([fill="none"]) {
    fill: currentColor;
  }
`;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type = "button",
      variant = "primary",
      size = "md",
      contentAlign = "center",
      fullWidth = false,
      loading = false,
      loadingLabel = "Processing",
      leadingIcon,
      trailingIcon,
      disabled,
      children,
      ...buttonProps
    },
    ref,
  ) => (
    <StyledButton
      {...buttonProps}
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      $variant={variant}
      $size={size}
      $fullWidth={fullWidth}
      $contentAlign={contentAlign}
    >
      <StableButtonContent>
        <ButtonContentState
          $visible={!loading}
          $contentAlign={contentAlign}
          aria-hidden={loading}
        >
          {leadingIcon}
          {children}
          {trailingIcon}
        </ButtonContentState>
        <ButtonContentState
          $visible={loading}
          $contentAlign={contentAlign}
          $overlay
          aria-hidden={!loading}
        >
          <LoadingSpinner aria-hidden="true" />
          <span>{loadingLabel}</span>
        </ButtonContentState>
      </StableButtonContent>
    </StyledButton>
  ),
);

Button.displayName = "Button";

const CancelIcon = () => (
  <svg
    aria-hidden="true"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M18 6L6 18M6 6L18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export interface CancelButtonProps extends Omit<
  ButtonProps,
  "variant" | "leadingIcon"
> {}

export const CancelButton = forwardRef<HTMLButtonElement, CancelButtonProps>(
  ({ children = "Cancel", ...buttonProps }, ref) => (
    <Button
      {...buttonProps}
      ref={ref}
      variant="secondary"
      leadingIcon={<CancelIcon />}
    >
      {children}
    </Button>
  ),
);

CancelButton.displayName = "CancelButton";

export const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${vars.spacing.md};
`;

export interface IconButtonProps extends Omit<
  ButtonProps,
  "fullWidth" | "leadingIcon" | "trailingIcon"
> {
  "aria-label": string;
}

const StyledIconButton = styled(Button)<{ size?: ButtonSize }>`
  width: ${({ size = "md" }) =>
    size === "sm" ? "32px" : size === "lg" ? "48px" : "40px"};
  min-width: ${({ size = "md" }) =>
    size === "sm" ? "32px" : size === "lg" ? "48px" : "40px"};
  padding: 0;
  border: 0;
`;

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ type = "button", variant = "ghost", children, ...buttonProps }, ref) => (
    <StyledIconButton {...buttonProps} ref={ref} type={type} variant={variant}>
      {children}
    </StyledIconButton>
  ),
);

IconButton.displayName = "IconButton";

export interface InlineIconButtonProps extends HTMLAttributes<HTMLSpanElement> {
  "aria-label": string;
  variant?: "ghost" | "danger";
}

const StyledInlineIconButton = styled.span<{
  $variant: "ghost" | "danger";
}>`
  display: inline-flex;
  width: 32px;
  min-width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: ${vars.radius.sm};
  cursor: default;

  ${({ $variant }) =>
    $variant === "danger" ? variantStyles.danger : variantStyles.ghost}

  &:focus-visible {
    outline: none;
    box-shadow: ${vars.shadow.focus};
  }

  svg [stroke]:not([stroke="none"]) {
    stroke: currentColor;
  }

  svg [fill]:not([fill="none"]) {
    fill: currentColor;
  }
`;

export const InlineIconButton = forwardRef<
  HTMLSpanElement,
  InlineIconButtonProps
>(({ variant = "ghost", onKeyDown, ...props }, ref) => (
  <StyledInlineIconButton
    {...props}
    ref={ref}
    role="button"
    tabIndex={0}
    $variant={variant}
    onKeyDown={(event) => {
      onKeyDown?.(event);
      if (
        !event.defaultPrevented &&
        (event.key === "Enter" || event.key === " ")
      ) {
        event.preventDefault();
        event.currentTarget.click();
      }
    }}
  />
));

InlineIconButton.displayName = "InlineIconButton";

export const BareIconButton = styled(ButtonBase).attrs({ type: "button" })`
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: var(--inzeloop-ds-color-background-transparent);
  cursor: default;
`;

export const PrimarySubmitButton = styled(Button).attrs({
  variant: "primary",
  size: "lg",
})<{
  width?: string;
  height?: string;
  bgColor?: string;
  textColor?: string;
}>`
  box-sizing: border-box;
  width: ${({ width }) => width || "100%"};
  height: ${({ height }) => height || "48px"};
  border-style: none;
  border-radius: ${vars.radius.lg};
  background-color: ${({ bgColor }) => bgColor || vars.color.action.primary};
  color: ${({ textColor }) => textColor || vars.color.text.inverse};
  font-size: ${vars.typography.size.px21};

  &:disabled {
    background-color: ${vars.color.status.successAction.disabled};
    opacity: 0.7;
    transform: none;
  }
`;

export const CompactActionButton = styled(Button).attrs({
  variant: "secondary",
  size: "sm",
})<{ color?: string }>`
  padding: ${vars.spacing.sm} ${vars.spacing.xl} ${vars.spacing.sm}
    var(--inzeloop-ds-spacing-20px);
  color: ${({ color }) => color || vars.color.action.primaryHover};

  &:disabled {
    border-color: ${vars.color.text.disabled};
    background: ${vars.color.background.hover};
    color: ${vars.color.text.disabled};
  }
`;

export const CompactCancelButton = styled(CancelButton).attrs({ size: "sm" })`
  width: auto;
  min-width: 0;
  align-self: stretch;
  flex: 1 1 0;
  padding: ${vars.spacing.sm} ${vars.spacing.xl};
`;

export const ValidationButton = styled(Button).attrs({
  variant: "danger",
  size: "sm",
})<{ validateLoading?: boolean }>`
  padding: ${vars.spacing.sm} ${vars.spacing.xl} ${vars.spacing.sm}
    var(--inzeloop-ds-spacing-20px);
  ${({ validateLoading }) =>
    validateLoading &&
    css`
      background-color: ${vars.color.feedback.error.border};
      color: ${vars.color.text.inverse};
    `}

  &:not(:disabled):hover {
    border-color: ${vars.color.action.danger};
    background: ${vars.color.action.danger};
    color: ${vars.color.text.inverse};
  }

  svg,
  img {
    height: 24px;
  }
`;

export const UpdateEvaluationButton = styled(CompactActionButton)`
  &:not(:disabled):hover svg path {
    stroke: ${vars.color.text.inverse} !important;
  }

  &:disabled svg path {
    stroke: ${vars.color.text.disabled} !important;
  }
`;

export const HeaderActionButton = styled(ButtonBase)<{
  variant?: "gradient" | "transparent" | "danger";
}>`
  display: flex;
  height: 48px;
  align-items: center;
  justify-content: center;
  gap: ${vars.spacing.xs};
  border: 0;
  border-radius: ${vars.radius.sm};
  padding: ${vars.spacing.md} ${vars.spacing.xl};
  background: ${({ variant }) =>
    variant === "gradient"
      ? vars.color.action.aiGradient
      : vars.color.background.page};
  color: ${({ variant }) =>
    variant === "transparent"
      ? vars.color.action.primaryHover
      : variant === "danger"
        ? vars.color.action.danger
        : vars.color.text.inverse};

  &:focus-visible {
    outline: none;
    box-shadow: ${vars.shadow.focus};
  }

  &:hover {
    background: ${({ variant }) =>
      variant === "danger"
        ? vars.color.action.danger
        : variant === "gradient"
          ? vars.color.action.aiGradientHover
          : vars.color.action.primaryHover};
    color: ${vars.color.text.inverse};
  }

  &:disabled {
    background: ${vars.color.background.disabledStrong};
    color: ${vars.color.text.muted};
    cursor: not-allowed;

    svg path {
      stroke: ${vars.color.text.muted};
    }
  }
`;

export const HeaderAddStepButton = styled(Button).attrs({
  variant: "secondary",
  size: "md",
})`
  width: 170px;
  min-width: 170px;
  height: 50px;
`;

export const ComparisonAiButton = styled(Button).attrs({
  variant: "ai",
  size: "lg",
})`
  min-width: 148px;
  height: 48px;
`;
